/**
 * Fait : les appels du client qui portent sur la BIBLIOTHÈQUE DE CONSIGNES —
 *   écrire, lister, chercher, relire à l'unité, modifier, supprimer.
 *
 *   La recherche voyage dans l'adresse, pas dans un corps : c'est une lecture,
 *   et une lecture doit pouvoir se remettre en favori et se recharger.
 *
 * Ne fait pas : il ne décide d'aucun droit — lire est ouvert à toute la
 *   famille, écrire n'appartient qu'à l'auteur, et c'est la ROUTE qui tranche,
 *   jamais un bouton caché. Il n'affiche rien et ne copie rien dans le
 *   presse-papier : c'est l'écran qui s'en charge, sans passer par le serveur.
 */

import { appelle } from './appel.ts';

/** Une consigne telle que l'écran la reçoit. */
export interface ConsigneVue {
  readonly id: string;
  readonly titre: string;
  readonly description: string;
  readonly texte: string;
  readonly version: number;

  /** Vrai si je l'ai écrite — donc si j'ai le droit de la réécrire. */
  readonly aMoi: boolean;
}

/** Ce qu'on envoie pour écrire ou réécrire une consigne. */
export interface DemandeDeConsigne {
  readonly titre: string;
  readonly description: string;
  readonly texte: string;
}

/**
 * Toute la bibliothèque, la dernière touchée en tête, ou seulement celles que
 * la recherche trouve — sur le titre, la description ET le texte.
 */
export function listeLesConsignes(recherche?: string) {
  /*
   * LE CHEMIN EST UN LITTÉRAL ENTIER, et la recherche s'y ajoute après.
   * Écrire `` `/api/prompts${filtre}` `` marcherait tout aussi bien à
   * l'exécution, mais le contrôle des portes ne pourrait plus rattacher cet
   * appel à sa route : il ramène toute interpolation à un segment de chemin,
   * et lisait donc « /api/prompts:x », une route qui n'existe pas. Mesuré le
   * 2026-08-25 — le contrôle avait raison, c'est l'appel qui a été refait,
   * jamais l'outil (`D-034`).
   */
  const chemin = '/api/prompts';
  const filtre =
    recherche === undefined || recherche.trim() === ''
      ? ''
      : `?recherche=${encodeURIComponent(recherche.trim())}`;

  return appelle<{ prompts: ConsigneVue[] }>(`${chemin}${filtre}`);
}

/** Une consigne à l'unité. */
export function litLaConsigne(consigneId: string) {
  return appelle<ConsigneVue>(`/api/prompts/${consigneId}`);
}

/** Écrit une consigne neuve. Son auteur est celui qui l'écrit. */
export function ecritUneConsigne(demande: DemandeDeConsigne) {
  return appelle<{ id: string }>('/api/prompts', { methode: 'POST', corps: demande });
}

/**
 * Réécrit une consigne — la sienne seulement, et avec sa version.
 *
 * La version part avec la demande parce qu'une consigne n'a AUCUN canal de
 * diffusion : le direct ne parle que par projet, et une consigne n'appartient
 * à aucun. Deux onglets ouverts sur la même consigne ne s'avertissent donc
 * pas ; c'est la version qui rend le conflit lisible au lieu d'écraser en
 * silence.
 */
export function modifieLaConsigne(
  consigneId: string,
  demande: DemandeDeConsigne & { readonly version: number },
) {
  return appelle<void>(`/api/prompts/${consigneId}`, { methode: 'PATCH', corps: demande });
}

/** Supprime une consigne — la sienne seulement. Rien ne la rattrape. */
export function supprimeLaConsigne(consigneId: string) {
  return appelle<void>(`/api/prompts/${consigneId}`, { methode: 'DELETE' });
}
